import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { MonthPicker } from '../ui/date-picker';
import {
  Plus, Trash2, User, Calendar, Check, ChevronsUpDown, Info,
} from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * SittingsEditor — full CRUD widget for the `asked_by_history` array on
 * an oral question. A "sitting" is one row: {surveyor, type, exam_month,
 * mmd_location, asked}. The parent component owns the array via `value`
 * + `onChange(newArray)`.
 *
 * Behaviour contract (matches backend `services/sitting_aggregator`):
 *   - Total asked = sum of each sitting's `asked` (defaults to 1).
 *   - Top-level question surveyor + exam_month get recomputed by the
 *     BACKEND from the latest sitting on save. Nothing to enforce here.
 *
 * Props
 *   value:              Array<AskedByHistoryEntry>
 *   onChange:           (nextArray) => void
 *   surveyors:          Array<{id, name, is_internal, ...}>
 *   defaultMmdLocation: string (used for new rows)
 *   convertTextToMonthInput / convertMonthInputToText: shared helpers
 *     from AdminQuestionsPage so month formats round-trip identically.
 *
 * A "sitting" is intentionally lightweight — no per-row auth or validation
 * beyond "surveyor required". Deep validation lives on the server.
 */
const SittingsEditor = ({
  value = [],
  onChange,
  surveyors = [],
  defaultMmdLocation,
  convertTextToMonthInput,
  convertMonthInputToText,
  testIdPrefix = 'sitting',
}) => {
  const totalAsked = value.reduce((sum, e) => sum + (parseInt(e.asked, 10) || 1), 0);

  const updateRow = (idx, patch) => {
    const next = value.map((row, i) => (i === idx ? { ...row, ...patch } : row));
    onChange(next);
  };

  const removeRow = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const addRow = () => {
    onChange([
      ...value,
      {
        surveyor_id: '',
        surveyor_type: 'external',
        surveyor_name: '',
        exam_month: '',
        mmd_location: defaultMmdLocation || null,
        asked: 1,
        added_at: new Date().toISOString(),
      },
    ]);
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-editor`}>
      <div className="flex items-start gap-2 text-xs text-gray-500">
        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span>
          Sittings are recorded automatically when questions are merged. Edit here
          only to correct historical records. The total <em>Asked</em> count on the
          question, plus the top-level surveyor &amp; exam month, are recalculated
          from this list on save.
        </span>
      </div>

      {value.length === 0 && (
        <div className="p-3 text-xs text-gray-400 text-center border border-dashed rounded-md" data-testid={`${testIdPrefix}-empty`}>
          No sittings recorded yet. Click <strong>+ Add sitting</strong> to add one.
        </div>
      )}

      {value.map((row, idx) => (
        <SittingRow
          key={idx}
          idx={idx}
          row={row}
          surveyors={surveyors}
          onChange={(patch) => updateRow(idx, patch)}
          onRemove={() => removeRow(idx)}
          convertTextToMonthInput={convertTextToMonthInput}
          convertMonthInputToText={convertMonthInputToText}
          testIdPrefix={testIdPrefix}
        />
      ))}

      <div className="flex items-center justify-between pt-1">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={addRow}
          className="h-8"
          data-testid={`${testIdPrefix}-add-btn`}
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Add sitting
        </Button>
        <Badge variant="secondary" className="text-xs" data-testid={`${testIdPrefix}-total-badge`}>
          Total Asked: <strong className="ml-1">{totalAsked}</strong>
        </Badge>
      </div>
    </div>
  );
};

/**
 * SittingRow — one editable row within the SittingsEditor.
 * Kept in a nested component so the searchable surveyor combobox has
 * its own local `open` state (each row's popover opens independently).
 */
const SittingRow = ({
  idx, row, surveyors, onChange, onRemove,
  convertTextToMonthInput, convertMonthInputToText, testIdPrefix,
}) => {
  const [surveyorOpen, setSurveyorOpen] = useState(false);

  const selectedSurveyor = surveyors.find((s) => s.id === row.surveyor_id);
  const displayName = selectedSurveyor?.name || row.surveyor_name || 'Select surveyor…';

  const handlePickSurveyor = (surveyor) => {
    onChange({
      surveyor_id: surveyor.id,
      surveyor_name: surveyor.name,
      // Auto-derive type from the surveyor's own is_internal flag if present,
      // else keep whatever the row already had (defaults to 'external').
      surveyor_type:
        typeof surveyor.is_internal === 'boolean'
          ? (surveyor.is_internal ? 'internal' : 'external')
          : row.surveyor_type,
    });
    setSurveyorOpen(false);
  };

  const monthInputValue = row.exam_month ? convertTextToMonthInput(row.exam_month) : '';

  return (
    <div
      className="grid grid-cols-12 gap-2 items-center p-2 border rounded-md bg-white"
      data-testid={`${testIdPrefix}-row-${idx}`}
    >
      {/* Surveyor combobox (searchable) — 5 cols */}
      <div className="col-span-5">
        <Popover open={surveyorOpen} onOpenChange={setSurveyorOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              className="w-full justify-between h-8 text-xs font-normal"
              data-testid={`${testIdPrefix}-surveyor-trigger-${idx}`}
            >
              <span className="flex items-center gap-1 truncate">
                <User className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{displayName}</span>
              </span>
              <ChevronsUpDown className="w-3 h-3 opacity-50 flex-shrink-0" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[280px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search surveyor…" className="h-8 text-xs" />
              <CommandList>
                <CommandEmpty>No surveyor found.</CommandEmpty>
                <CommandGroup>
                  {surveyors.map((s) => (
                    <CommandItem
                      key={s.id}
                      value={s.name}
                      onSelect={() => handlePickSurveyor(s)}
                      data-testid={`${testIdPrefix}-surveyor-option-${idx}-${s.id}`}
                    >
                      <Check className={cn('mr-2 h-3 w-3', row.surveyor_id === s.id ? 'opacity-100' : 'opacity-0')} />
                      <span className="text-xs">{s.name}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Type toggle — 2 cols */}
      <div className="col-span-2">
        <select
          value={row.surveyor_type || 'external'}
          onChange={(e) => onChange({ surveyor_type: e.target.value })}
          className="w-full h-8 text-xs border rounded-md px-1 bg-white"
          data-testid={`${testIdPrefix}-type-${idx}`}
        >
          <option value="internal">Internal</option>
          <option value="external">External</option>
        </select>
      </div>

      {/* Exam month — 3 cols */}
      <div className="col-span-3">
        <MonthPicker
          value={monthInputValue}
          onChange={(v) =>
            onChange({ exam_month: v ? convertMonthInputToText(v) : '' })
          }
          placeholder="Month"
          fromYear={2015}
          toYear={new Date().getFullYear()}
          data-testid={`${testIdPrefix}-month-${idx}`}
        />
      </div>

      {/* Asked count — 1 col */}
      <div className="col-span-1">
        <Input
          type="number"
          min={1}
          value={row.asked ?? 1}
          onChange={(e) =>
            onChange({ asked: Math.max(1, parseInt(e.target.value, 10) || 1) })
          }
          className="h-8 text-xs text-center px-1"
          data-testid={`${testIdPrefix}-asked-${idx}`}
          title="How many times this surveyor asked this question in this sitting"
        />
      </div>

      {/* Delete — 1 col */}
      <div className="col-span-1 flex justify-end">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onRemove}
          className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
          title="Remove this sitting"
          data-testid={`${testIdPrefix}-remove-${idx}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>

      {/* Row-level exam_month display + warning */}
      {!row.surveyor_id && (
        <div className="col-span-12 -mt-1 text-[10px] text-amber-600 flex items-center gap-1">
          <Info className="w-3 h-3" /> Pick a surveyor to save this sitting.
        </div>
      )}
      {row.exam_month && (
        <div className="col-span-12 -mt-1 text-[10px] text-gray-400 flex items-center gap-1 pl-1">
          <Calendar className="w-3 h-3" /> {row.exam_month}
        </div>
      )}
    </div>
  );
};

export default SittingsEditor;
